/**
 * Hook de **edición de orden** (`PATCH /api/v1/orders/<id>`), semántica parcial.
 *
 * Envuelve `updateOrder` en un `useMutation`. Tras éxito invalida los listados
 * (la edición puede cambiar campos visibles en la tabla) y el detalle de esa
 * orden (`docs/frontend_conventions.md`, Datos). El cuerpo es un subconjunto de
 * `OrderWriteBody`: la pantalla envía SOLO los campos que el usuario puede editar
 * (permisos granulares); el resto ni se incluye. El `ApiError` se propaga para que
 * la pantalla muestre `{error_code, message}`.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { getActiveSedeId } from "@/lib/sede";

import { updateOrder } from "../api";
import type { OrderDetail, OrderWriteBody } from "../types";
import { ORDERS_ROOT_KEY, orderDetailQueryKey } from "./queryKeys";

interface UpdateVariables {
  id: number;
  body: Partial<OrderWriteBody>;
}

export function useUpdateOrder() {
  const queryClient = useQueryClient();
  const sedeId = getActiveSedeId();

  return useMutation<OrderDetail, Error, UpdateVariables>({
    mutationFn: ({ id, body }) => updateOrder(id, body),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ORDERS_ROOT_KEY });
      void queryClient.invalidateQueries({
        queryKey: orderDetailQueryKey(sedeId, id),
      });
    },
  });
}
